/**
 * @file The Plugin Pair Selector component.
 * @author Heino Lenting
*/
import * as tslib_1 from "tslib";
import { EventEmitter, OnDestroy } from '@angular/core';
import { AbstractComponent } from '../../../core/helpers/abstract-component';
import { EventsService } from '../../../core/event.service';
import { ForecastService } from '../../../forecast/forecast.service';
import { DomainService } from '../../../domain.service';
import { LocalStorageService } from '../../../core/local-storage.service';
import { PluginPairService } from '../../plugin-pair.service';
import { AlertsService } from '../../../core/alerts.service';
var PluginPairSelectorComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PluginPairSelectorComponent, _super);
    /**
     * Creates an instance of PluginPairSelectorComponent.
     * @param {PluginPairService} pluginPairService The plugin pair service.
     * @param {DomainService} domainService The domain service is used to get all functions.
     * @param {ForecastService} forecastService The forecast service is used to get the forecast functions.
     * @param {EventsService} eventsService The events service is used to fire an event when the pair selection has changed.
     * @memberOf PluginPairSelectorComponent
     */
    function PluginPairSelectorComponent(pluginPairService, domainService, forecastService, localStorageService, eventsService, alertsService) {
        var _this = _super.call(this, alertsService) || this;
        _this.pluginPairService = pluginPairService;
        _this.domainService = domainService;
        _this.forecastService = forecastService;
        _this.localStorageService = localStorageService;
        _this.eventsService = eventsService;
        _this.alertsService = alertsService;
        /**
         * The selectable functions.
         * @type {Entity[]}
         * @memberOf PluginPairSelectorComponent
         */
        _this.functionEntities = [];
        return _this;
    }
    /**
     * Is called after the block name is set and the configuration is loaded.
     */
    PluginPairSelectorComponent.prototype.afterInitialize = function () {
        var selected = this.localStorageService.readProperty('local', PluginPairSelectorComponent.STORED_SELECTED_PAIR, undefined);
        if (selected) {
            this.pluginPairService.selectedPluginPair = this.pluginPairService.pluginPairs.find(function (pair) { return pair.name === selected; });
            this.setFunctions();
        }
        else {
            this.localStorageService.updateProperty('local', PluginPairSelectorComponent.STORED_SELECTED_PAIR, this.pluginPairService.selectedPluginPair);
        }
        this.pluginPairSelectorChanged = this.eventsService.register('plugin-selector-changed');
    };
    /**
     * Used to notify other of a plugin pair change.
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.pluginPairChanged = function () {
        this.pluginPairSelectorChanged.emit(true);
        this.localStorageService.updateProperty('local', PluginPairSelectorComponent.STORED_SELECTED_PAIR, this.pluginPairService.selectedPluginPair.name);
        if (this.pluginPairService.selectedPluginPair.filterMethod === 'function') {
            this.setFunctions();
        }
    };
    /**
     * Used to notify other of a function entity change.
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.functionEntityChanged = function () {
        this.pluginPairSelectorChanged.emit(true);
        if (this.pluginPairService.selectedFunction) {
            this.localStorageService.updateProperty('local', PluginPairSelectorComponent.STORED_SELECTED_FUNCTION, this.pluginPairService.selectedFunction['Oid']);
        }
        else {
            this.localStorageService.deleteProperty('local', PluginPairSelectorComponent.STORED_SELECTED_FUNCTION);
        }
    };
    /**
    * The current function Entity.
     * @memberOf PluginPairSelectorComponent
    */
    PluginPairSelectorComponent.prototype.functionEntityName = function () {
        return (this.pluginPairService.selectedFunction) ? this.pluginPairService.selectedFunction['Name'] : undefined;
    };
    /**
    * Check if the function select is needed.
     * @memberOf PluginPairSelectorComponent
    */
    PluginPairSelectorComponent.prototype.showFunctionSelect = function () {
        return (this.pluginPairService.selectedPluginPair.filterMethod === 'function' && this.functionEntities);
    };
    /**
     * Is used to disable or enable the refresh button for the plugin pair selector.
     * @returns {Boolean} When true refresh button is enabled otherwise disabled.
     */
    PluginPairSelectorComponent.prototype.showPluginPairRefresh = function () {
        if (this.pluginPairService.selectedPluginPair.name === 'PLUGIN_EMPTY' || this.pluginPairService.selectedPluginPair.name === 'PLUGINS_PAIR_EMPTY') {
            return false;
        }
        return (this.pluginPairService.selectedPluginPair.filterMethod === 'none');
    };
    /**
     * Is used to disable or enable the refresh button.
     * @returns {Boolean} When true refresh button is enabled otherwise disabled.
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.refreshable = function () {
        if (this.pluginPairService.selectedPluginPair.name !== 'PLUGINS_PAIR_PROGNOSES_CONTROLS') {
            return true;
        }
        return this.pluginPairService.selectedFunction != null;
    };
    /**
    * Refresh the plugins.
     * @memberOf PluginPairSelectorComponent
    */
    PluginPairSelectorComponent.prototype.refreshPlugins = function () {
        this.pluginPairSelectorChanged.emit(true);
    };
    /**
    * Clear the selection to improve performance.
     * @memberOf PluginPairSelectorComponent
    */
    PluginPairSelectorComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        this.pluginPairService.clearSelected();
    };
    /**
     * Retrieves the relevant functions.
     * @private
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.setFunctions = function () {
        var _this = this;
        var dataSource = this.configuration.data.dataSource;
        this.functionEntities.length = 0;
        switch (this.pluginPairService.selectedPluginPair.name) {
            case 'PLUGINS_PAIR_PROGNOSES_CALLS':
            case 'PLUGINS_PAIR_PROGNOSES_CORRECTIONS':
            case 'PLUGINS_PAIR_PROGNOSES_CONTROLS':
            case 'PLUGINS_PAIR_CAPACITY_SHRINKAGE':
            case 'PLUGINS_PAIR_CHECK_SCRIPT':
            case 'PLUGINS_PAIR_CAPACITY_FORECAST':
            case 'PLUGINS_PAIR_CAPACITY_FORECAST_EXTRA':
            case 'PLUGINS_PAIR_SLA_FORECAST':
            case 'PLUGINS_PAIR_SLA_FORECAST_EXTRA':
                if (!this.forecastService.entityManager) {
                    this.alertsService.createAlert('danger', 'APP.GRIDS.NO_FORECAST_SERVICE');
                    return;
                }
                this.forecastService.entityManager.executeQuery(dataSource, function (response) {
                    response.results.forEach(function (entity) {
                        _this.functionEntities.push(entity);
                    });
                    _this.functionEntities.sort(_this.sortByProperty('Name'));
                    _this.setSelectedFunction();
                });
                break;
            case 'PLUGIN_CONTROLS':
            case 'PLUGIN_SHIFTS':
            case 'PLUGIN_SHIFTS_EXTRA':
                this.domainService.entityManager.executeQuery('FunctionItems', function (response) {
                    response.results.forEach(function (entity) {
                        _this.functionEntities.push(entity);
                    });
                    _this.functionEntities.sort(_this.sortByProperty('Name'));
                    _this.setSelectedFunction();
                });
                break;
        }
    };
    /**
     * Sets the previously selected function, if any.
     * @private
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.setSelectedFunction = function () {
        var selected = this.localStorageService.readProperty('local', PluginPairSelectorComponent.STORED_SELECTED_FUNCTION, undefined);
        if (selected) {
            this.pluginPairService.selectedFunction = this.functionEntities.find(function (f) { return f['Oid'] === selected; });
            this.refreshPlugins();
        }
    };
    /**
     * Returns an array sort function that sorts by the given property.
     * @private
     * @param {string} property The name of the property
     * @returns A function that can be used as parameter of Array.prototype.sort()
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.prototype.sortByProperty = function (property) {
        return function (value1, value2) {
            if (value1[property] < value2[property]) {
                return -1;
            }
            if (value1[property] > value2[property]) {
                return 1;
            }
            return 0;
        };
    };
    /**
     * The string is used to update and read the selected plugin pair in the local storage.
     * @private
     * @static
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.STORED_SELECTED_PAIR = 'selected-plugin-pair';
    /**
     * The string is used to update and read the selected function in the local storage.
     * @private
     * @static
     * @memberOf PluginPairSelectorComponent
     */
    PluginPairSelectorComponent.STORED_SELECTED_FUNCTION = 'selected-plugin-function';
    return PluginPairSelectorComponent;
}(AbstractComponent));
export { PluginPairSelectorComponent };
